/**
 * ActivityStats (N4c) — one component, used on the Activity Stats page and (in a
 * `compact` variant) at the bottom of the dashboard. Three period boxes — Today ·
 * This week · This month — each showing calls / texts / emails, with a single
 * "whose" selector.
 *
 * Permissions are a DELIBERATE exception to the shared-files model: a rep sees
 * only their own numbers, the selector isn't shown to them, and the endpoint 403s
 * if a rep requests anyone else. Admin sees everyone and switches freely. The
 * gate is enforced server-side; hiding the control is only cosmetic.
 *
 * Selection can be controlled by a parent (Activity Stats page → URL); left
 * uncontrolled (dashboard) it keeps internal state. Periods are tz-aware per the
 * viewer with a Sunday week start — all computed in lib/time and sent to the
 * server as instants; the server returns just the nine numbers.
 *
 * CALLS AND TEXTS come from the chips on a sales note. EMAILS do not: they are
 * counted from outbound client mail, so there is no email chip to tick and
 * nothing to forget. Both are counted in SQL (migration 043) rather than by
 * fetching rows, which is what used to cap the month silently at 1000. Owns a 30s refresh cadence (these numbers move a few times a day) — so
 * the Stats page is live AND the dashboard copy is NOT dragged onto the 3s poll,
 * which would fire a stats query every tick against the database.
 */

import React, { useEffect, useState } from 'react';
import { CRM } from '../lib/theme';
import { gate } from './States';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { userTz, todayInTz, addDaysStr, weekdayIndex, startOfDayInTz } from '../lib/time';
import { Icons } from '../lib/icons';

const C = { ...CRM, call: CRM.green, text_: CRM.cyan, email: CRM.violet };

type Bucket = { calls: number; texts: number; emails: number };
const ZERO: Bucket = { calls: 0, texts: 0, emails: 0 };

interface ActivityStatsProps {
  compact?: boolean;
  rep?: string;                           // controlled selection ('' = role default)
  onChangeRep?: (rep: string) => void;    // controlled setter (writes the URL)
  users?: any[];                          // pass in to avoid a duplicate users fetch (dashboard)
}

export function ActivityStats({ compact = false, rep, onChangeRep, users: usersProp }: ActivityStatsProps) {
  const { user } = useAuth();

  // Own refresh cadence: 30s. Keeps the Stats page live without polling the
  // database every 3 seconds when this rides along on the dashboard.
  const [selfTick, setSelfTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setSelfTick((t) => t + 1), 30000);
    return () => window.clearInterval(id);
  }, []);
  const tz = userTz(user);
  const isAdmin = user?.role === 'admin';
  const roleDefault = isAdmin ? 'all' : (user?.id ?? 'all');

  // Controlled when a parent passes onChangeRep; otherwise internal. An empty
  // controlled value means "use the role default".
  const [internalRep, setInternalRep] = useState<string>(roleDefault);
  const selected = rep !== undefined ? (rep === '' ? roleDefault : rep) : internalRep;
  const changeRep = (r: string) => {
    if (onChangeRep) onChangeRep(r === roleDefault ? '' : r); // keep URL clean at the default
    else setInternalRep(r);
  };

  // A rep can never resolve to anyone but themselves (server enforces this too).
  const effectiveRep = isAdmin ? selected : (user?.id ?? '');

  // Users for the selector — admin only, and skip if the caller already has them.
  const { data: fetchedUsers } = useApi<any[]>(
    async (api) => (await api.getUsers()).data || [],
    { skip: !isAdmin || !!usersProp }
  );
  const users = usersProp ?? fetchedUsers ?? [];
  const repName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email : '';
  };

  const { data: stats, loading: statsLoading, error: statsError, refetch: refetchStats } = useApi<{ today: Bucket; week: Bucket; month: Bucket } | null>(
    async (api) => {
      const today = todayInTz(tz);
      const weekStartDate = addDaysStr(today, -weekdayIndex(today)); // Sunday week start
      const monthStartDate = today.slice(0, 7) + '-01';
      return (await api.getActivityStats({
        rep: effectiveRep,
        todayStart: startOfDayInTz(today, tz),
        weekStart: startOfDayInTz(weekStartDate, tz),
        monthStart: startOfDayInTz(monthStartDate, tz),
      })).data ?? null;
    },
    { skip: !user, dependencies: [effectiveRep, selfTick] }
  );

  // ZERO is a real answer for a rep with a quiet morning and an INVENTED one when
  // the request failed. The numbers are the entire content of this panel, so it
  // must not show nine zeros that read as "no calls yet".
  const statsGate = gate('Activity stats', statsLoading, statsError, !!stats, refetchStats);

  const periods: { label: string; data: Bucket }[] = [
    { label: 'Today', data: stats?.today ?? ZERO },
    { label: 'This week', data: stats?.week ?? ZERO },
    { label: 'This month', data: stats?.month ?? ZERO },
  ];

  const whoLabel = selected === 'all' ? 'All reps combined' : (isAdmin ? repName(selected) : 'Your activity');

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', padding: compact ? '12px 14px' : '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <div style={{ fontSize: compact ? '13px' : '14px', fontWeight: 600, color: C.text }}>Activity{compact ? '' : ' Stats'}</div>
        <span style={{ fontSize: '12px', color: C.muted }}>· {whoLabel}</span>
        <div style={{ flex: 1 }} />
        {/* Selector — admin only. A rep never sees it (and can't request others). */}
        {isAdmin && (
          <select
            value={selected}
            onChange={(e) => changeRep(e.target.value)}
            aria-label="Whose activity"
            style={{ padding: '5px 8px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '7px', color: C.text, fontSize: '12px', cursor: 'pointer' }}
          >
            <option value="all">All reps combined</option>
            {users.map((u) => <option key={u.id} value={u.id}>{repName(u.id)}</option>)}
          </select>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: compact ? '10px' : '12px' }}>
        {/* Spans the three-column grid it sits in, rather than pretending to be
            one of the period tiles. */}
        {statsGate && <div style={{ gridColumn: '1 / -1' }}>{statsGate}</div>}
        {!statsGate && periods.map((p) => (
          <div key={p.label} style={{ background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '7px', padding: compact ? '10px' : '12px' }}>
            <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px', letterSpacing: '.4px' }}>{p.label}</div>
            <div style={{ display: 'flex', gap: compact ? '10px' : '14px' }}>
              <Metric icon={<Icons.phone size={13} />} value={p.data.calls} label="calls" color={C.call} />
              <Metric icon={<Icons.message size={13} />} value={p.data.texts} label="texts" color={C.text_} />
              <Metric icon={<Icons.email size={13} />} value={p.data.emails} label="emails" color={C.email} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ icon, value, label, color }: { icon: React.ReactNode; value: number; label: string; color: string }) {
  return (
    <div>
      <div style={{ fontSize: '18px', fontWeight: 700, color }}>{value}</div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: 'var(--crm-muted)' }}>
        <span style={{ color, display: 'inline-flex' }}>{icon}</span>{label}
      </div>
    </div>
  );
}
